import React, { useEffect, useRef, useState } from 'react';

interface ReportConversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage: string;
}

interface ReportConversationsStripProps {
  reportId: string | undefined;
  activeSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  /** Bump to force a refetch (e.g. after a new session is created or first message lands). */
  refreshKey?: number | string;
}

const ReportConversationsStrip: React.FC<ReportConversationsStripProps> = ({
  reportId,
  activeSessionId,
  onSelectSession,
  refreshKey,
}) => {
  const [sessions, setSessions] = useState<ReportConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reportId) {
      setSessions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('riskninja-token');
        if (!token) return;

        const response = await fetch(`${apiUrl}/api/chat/sessions/by-report/${reportId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) return;

        const data = await response.json();
        if (cancelled) return;
        if (data.success && Array.isArray(data.sessions)) {
          setSessions(data.sessions);
        }
      } catch (error) {
        console.error('Failed to load report conversations:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [reportId, refreshKey]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!reportId) return null;
  if (!loading && sessions.length === 0) return null;

  const activeSession = sessions.find(s => s.id === activeSessionId);
  const triggerLabel = activeSession?.title
    || `${sessions.length} ${sessions.length === 1 ? 'conversation' : 'conversations'}`;

  return (
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
      <div className="flex items-center gap-3">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-accent dark:text-dark-muted whitespace-nowrap">
          Past conversations
        </h5>
        <div ref={containerRef} className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
          >
            <span className="truncate text-secondary dark:text-dark-text">
              {triggerLabel}
            </span>
            <span className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-accent dark:text-dark-muted">
                {sessions.length}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`text-accent dark:text-dark-muted transition-transform ${open ? 'rotate-180' : ''}`}
              >
                <path d="M7 10L12 15L17 10H7Z" />
              </svg>
            </span>
          </button>

          {open && (
            <div className="absolute z-20 mt-1 left-0 right-0 max-h-80 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
              {sessions.map((session) => {
                const isActive = session.id === activeSessionId;
                return (
                  <button
                    key={session.id}
                    type="button"
                    onClick={() => {
                      onSelectSession(session.id);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-secondary dark:text-dark-text truncate">
                        {session.title || 'Untitled Chat'}
                      </div>
                      {isActive && (
                        <span className="flex-shrink-0 text-[10px] uppercase tracking-wide font-semibold text-blue-600 dark:text-blue-400">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-accent dark:text-dark-muted">
                      <span>{new Date(session.updatedAt).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>{session.messageCount} msg</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportConversationsStrip;
